import { hasAnyAccessToResource, hasViewAccessToResource } from "api/general";
import { useWallets } from "api/wallets";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ImportJsonModal } from "components/ImportJsonModal/ImportJsonModal";
import { ExportProgressModal } from "components/Layout/Achievement/AchievementList/ImportAchievements/ExportProgressModal";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useTableSelectable } from "components/Table/useTableSelectable";
import { TargetTenantModal } from "components/TargetTenantModal/TargetTenantModal";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { setImportedWallets } from "reduxState/wallets/wallets";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { WALLET_TYPES_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { BEWalletTypeFormat, WalletTypeListItem } from "types/wallets";
import { exportToJsonFile } from "utilities/exportToJson";
import { links } from "utilities/links";

function CreateItems(items: WalletTypeListItem[]): Row[] {
  return items.map((item) => ({
    cells: [
      { content: item.name, property: "name" },
      {
        content: <StatusValue status={item.active} />,
        property: "status",
      },
      { content: item.unitPluralName, property: "unitPluralName" },
      { content: item.code, property: "code" },
    ],
    id: item.walletTypeId,
    data: item,
    metadata: { disabled: !item.active },
  }));
}

export function WalletTypeList() {
  const { message } = useMessage();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(WALLET_TYPES_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[WALLET_TYPES_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      WALLET_TYPES_LIST_ID,
    );

    setFilters(filters);
  };
  const {
    rowsSelected,
    selectRow,
    unselectRow,
    selectAll,
    unselectAll,
    disabled,
    clearSelection,
  } = useTableSelectable();
  const [isSelectable, setIsSelectable] = useState(false);
  const [isProcessingExport, setIsProcessingExport] = useState(false);
  const [isImportJsonModal, setIsImportJsonModal] = useState(false);
  const [isDifferentTenantModalOpen, setIsDifferentTenantModalOpen] =
    useState(false);
  const [walletsExportData, setWalletsExportData] = useState<
    BEWalletTypeFormat[]
  >([]);

  const { useWalletTypeList, getWalletTypeBatchData } = useWallets();
  const { data, error } = useWalletTypeList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
    dynamicSearch,
  });

  const items = data?.items ? CreateItems(data.items) : [];

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      headerText: message({ id: "unit" }),
      property: "unitPluralName",
      isSortable: true,
    },
    {
      headerText: message({ id: "walletCode" }),
      property: "code",
      isSortable: true,
    },
  ]);

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, WALLET_TYPES_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  const hasViewLanguagePermission = hasViewAccessToResource("LANGUAGE");
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");

  return (
    <>
      <TopHeader title={message({ id: "typesOfWallets" })} />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "typesOfWallets" })}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "wallet-type-name-filter",
            },
            {
              query: "unitPluralName",
              label: message({ id: "unit" }),
              type: "text",
            },
            {
              query: "code",
              label: message({ id: "walletCode" }),
              type: "text",
            },
          ]}
          setFilters={setSelectedFilters}
          total={data?.total}
          data-test-id="wallets-list"
        />
        <SubheaderMenuButton
          text={message({ id: "addWalletType" })}
          menuItems={[
            {
              text: message({ id: "addWalletType" }),
              onClick: () => navigate(links.addWalletType),
            },
            {
              text: message({ id: "importWalletType" }),
              onClick: () => setIsImportJsonModal(true),
            },
          ]}
          disabled={!hasAnyLanguagePermission}
          data-test-id="wallets-list"
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          {isSelectable ? (
            <>
              <SubheaderActionButton
                text={message({ id: "close" }).toUpperCase()}
                onClick={() => setIsSelectable(false)}
              />
              <SubheaderMenuButton
                text={`${message({ id: "bulkActions" }).toUpperCase()}... (${rowsSelected.length})`}
                menuItems={[
                  {
                    text: message({ id: "downloadJsonFile" }),
                    onClick: async () => {
                      setIsProcessingExport(true);
                      exportToJsonFile({
                        data: await getWalletTypeBatchData(
                          rowsSelected.map(({ data: { walletTypeId } }) => ({
                            walletTypeId,
                          })),
                        ),
                        name: "walletTypes",
                        callback: () => {
                          clearSelection();
                          setIsProcessingExport(false);
                          return setIsSelectable(false);
                        },
                      });
                    },
                  },
                  {
                    text: message({ id: "copyToDifferentTenant" }),
                    onClick: async () => {
                      setIsProcessingExport(true);
                      const data = await getWalletTypeBatchData(
                        rowsSelected.map(({ data: { walletTypeId } }) => ({
                          walletTypeId,
                        })),
                      );

                      setWalletsExportData(
                        (data || []) as BEWalletTypeFormat[],
                      );
                      setIsDifferentTenantModalOpen(true);
                    },
                  },
                ]}
                hideArrow
                disabled={rowsSelected.length === 0}
              />
            </>
          ) : (
            <SubheaderActionButton
              text={message({ id: "bulkActions" }).toUpperCase()}
              onClick={() => setIsSelectable(true)}
              disabled={hasViewLanguagePermission}
            />
          )}
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: WALLET_TYPES_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={WALLET_TYPES_LIST_ID}
        data-test-id="wallets-list"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editWalletType(id as string)),
              disableCondition: () => !hasAnyLanguagePermission,
              label: message({ id: "edit" }),
            },
          ],
        }}
        columns={data && headItems}
        onRowClick={(id: string) => {
          hasAnyLanguagePermission && navigate(links.wallet(id as string));
        }}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        selectable={
          isSelectable
            ? {
                rowsSelected,
                selectRow,
                unselectRow,
                selectAll,
                unselectAll,
                disabled,
                clearSelection,
              }
            : undefined
        }
        clearFilters={() => setFilters([])}
      />
      <ExportProgressModal
        isProcessingExport={isProcessingExport}
        setIsProcessingExport={setIsProcessingExport}
      />
      <ImportJsonModal
        headerMessage="importWalletTypes"
        isOpen={isImportJsonModal}
        onClose={() => setIsImportJsonModal(false)}
        importTooBigErrorMessage="maxImportWalletTypesCountReached"
        setJsonAction={setImportedWallets}
        link={links.walletsImportList}
      />
      <TargetTenantModal
        isOpen={isDifferentTenantModalOpen}
        headerText={message(
          {
            id:
              rowsSelected.length === 1
                ? "copyOneWalletTypeToTargetTenant"
                : "copyWalletTypesAndViewTargetTenant",
          },
          {
            count: rowsSelected.length,
          },
        )}
        onClose={() => {
          setIsDifferentTenantModalOpen(false);
          setIsProcessingExport(false);
        }}
        setJsonAction={setImportedWallets}
        data={walletsExportData}
        link={links.walletsImportList}
        infoText={message({ id: "copiedWalletTypesInfo" })}
        buttonText={message({
          id:
            rowsSelected.length === 1
              ? "copyWalletTypeAndViewTargetTenant"
              : "copyWalletTypesAndViewTargetTenant",
        })}
      />
    </>
  );
}
